// INSIDO: insido design tokens (07-tema-insido).
//
// T-01 decision — EXPLICIT OVERRIDE, not a new Material 3 seed.
// The client builds its palette from a single M3 seed (see materialTheme.ts),
// but a tonal seed cannot reproduce the prototype's exact neutral surfaces
// (#181819 channels, #141415 chat, #111111 ink, ...). So we keep M3 for the
// accent-derived tokens (primary/secondary/tertiary come from the blue seed
// #3c7de6 set in components/branding) and override ONLY the neutral surface /
// outline / on-surface ramp with the prototype's exact hex, for both themes.
//
// These overrides are applied in LoadTheme AFTER the generated M3 variables so
// they win. We also expose the prototype's semantic tokens as `--insido-*` and
// the 4 brand colours as `--brand-*` (single point of truth for module 08).
//
// Source: docs/mockup/prototipo-v3.html  :root (dark) and body.light (light).

/** Prototype palette, dark theme (`:root`) */
const DARK = {
  ink: "#111111",
  inset: "#0c0c0c",
  chatBg: "#141415",
  surface: "#181819",
  card2: "#1b1b1d",
  raised: "#202022",
  raised2: "#29292c",
  line: "#313135",
  lineSoft: "#1e1e20",
  text: "#f1f1f2",
  text2: "#d3d3d6",
  // INSIDO (QA contrast pass): was #8c8c93 — 5.65:1 on ink but only 4.34:1 on
  // the raised2 surface the settings sidebar sits on, under the 4.5:1 AA
  // minimum. #96969d clears it there (4.94:1) and stays a neutral grey.
  muted: "#96969d",
  faint: "#5a5a60",
  accent: "#3c7de6",
  accentBright: "#5a93ec",
  accentSoft: "rgba(60, 125, 230, 0.15)",
  accentLine: "rgba(60, 125, 230, 0.34)",
  brandRed: "#ec6359",
  brandAmber: "#f2b550",
  brandGreen: "#4cb878",
  brandBlue: "#5a93ec",
  markHead: "#f1f1f2",
} as const;

/** Prototype palette, light theme (`body.light`) */
const LIGHT = {
  ink: "#e3e3e5",
  inset: "#ffffff",
  chatBg: "#ffffff",
  surface: "#f5f5f6",
  card2: "#ffffff",
  raised: "#ececee",
  raised2: "#e2e2e5",
  line: "#d8d8db",
  lineSoft: "#e7e7ea",
  text: "#16161a",
  text2: "#33333a",
  // INSIDO (QA contrast pass): was #6b6b70 — fine on white (5.30:1) but only
  // 4.10:1 on the raised2 surface the settings sidebar and member rows sit on,
  // under the 4.5:1 AA minimum. #5f5f66 clears it everywhere (4.90:1 there,
  // 6.33:1 on white) without visibly changing the neutral.
  muted: "#5f5f66",
  faint: "#9a9aa0",
  accent: "#3c7de6",
  accentBright: "#2f6fd6",
  accentSoft: "rgba(60, 125, 230, 0.12)",
  accentLine: "rgba(60, 125, 230, 0.30)",
  brandRed: "#e0483d",
  brandAmber: "#cf8f1e",
  brandGreen: "#2f9d5b",
  brandBlue: "#3c7de6",
  markHead: "#111111",
} as const;

type Palette = Record<keyof typeof DARK, string>;

/** Convert "#rrggbb" to an "r, g, b" triplet (for the --mdui-color-* vars) */
function triplet(hex: string): string {
  const m = /#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  if (!m) return "0, 0, 0";
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

/**
 * Map the prototype neutrals onto the Material 3 surface ramp. Wherever an
 * insido surface should appear, the app already reads one of these M3 tokens,
 * so overriding them re-skins the whole chrome with the prototype neutrals.
 */
function materialSurfaceOverrides(p: Palette): Record<string, string> {
  return {
    surface: p.surface,
    "surface-dim": p.chatBg,
    "surface-bright": p.raised2,
    "surface-container-lowest": p.inset,
    "surface-container-low": p.chatBg,
    "surface-container": p.card2,
    "surface-container-high": p.raised,
    "surface-container-highest": p.raised2,
    "on-surface": p.text,
    "on-surface-variant": p.muted,
    outline: p.line,
    "outline-variant": p.lineSoft,
  };
}

/**
 * Build every insido CSS variable for the active theme.
 * @param darkMode Whether the dark theme is active
 */
export function createInsidoVariables(
  darkMode: boolean,
): Record<string, string> {
  const p = darkMode ? DARK : LIGHT;

  const out: Record<string, string> = {
    // --- semantic prototype tokens (consumed by insido components + module 08)
    "--insido-ink": p.ink,
    "--insido-inset": p.inset,
    "--insido-chat-bg": p.chatBg,
    "--insido-surface": p.surface,
    "--insido-card-2": p.card2,
    "--insido-raised": p.raised,
    "--insido-raised-2": p.raised2,
    "--insido-line": p.line,
    "--insido-line-soft": p.lineSoft,
    "--insido-text": p.text,
    "--insido-text-2": p.text2,
    "--insido-muted": p.muted,
    "--insido-faint": p.faint,
    "--insido-accent": p.accent,
    "--insido-accent-bright": p.accentBright,
    "--insido-accent-soft": p.accentSoft,
    "--insido-accent-line": p.accentLine,
    "--insido-mark-head": p.markHead,

    // --- 4 brand colours (single point of truth, consumed by module 08)
    "--brand-red": p.brandRed,
    "--brand-amber": p.brandAmber,
    "--brand-green": p.brandGreen,
    "--brand-blue": p.brandBlue,

    // --- presence = brand colours (light/dark aware), see UserStatus.tsx
    "--brand-presence-online": p.brandGreen,
    "--brand-presence-idle": p.brandAmber,
    "--brand-presence-busy": p.brandRed,
    "--brand-presence-focus": p.brandBlue,

    // --- shape (RF-06)
    "--radius": "18px",
    "--radius-pill": "999px",
  };

  // Override the M3 neutral ramp (both the hex vars and the mdui triplets).
  for (const [key, hex] of Object.entries(materialSurfaceOverrides(p))) {
    out[`--md-sys-color-${key}`] = hex;
    out[`--mdui-color-${key}`] = triplet(hex);
  }

  return out;
}
